import { Body, H2, H4, SearchBar, lightThemeClass } from '@cointracker/ui';
import type { CollectionEntry } from 'astro:content';
import classNames from 'clsx';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { EntriesSection } from './EntriesSection';
import {
  EntryMarkdownH2,
  EntryMarkdownH4,
  EntryMarkdownLi,
  EntryMarkdownOl,
  EntryMarkdownP,
  EntryMarkdownTable,
  EntryMarkdownTbody,
  EntryMarkdownTd,
  EntryMarkdownTh,
  EntryMarkdownThead,
  EntryMarkdownTr,
  EntryMarkdownUl,
  learnEntryContent,
  learnEntryDescription,
  otherGlossaryTermsContainer,
  otherGlossaryTermsSearchBar,
} from './LearnEntry.css';
import { learnContainer } from './LearnLanding.css';
import { useSearch } from './hooks/useSearch';

export const LearnEntry = ({
  entry,
  entries,
}: {
  entry: CollectionEntry<'learn'>;
  entries: CollectionEntry<'learn'>[];
}) => {
  const { filteredEntries, searchText, setSearchText } = useSearch(entries);

  return (
    <main className={classNames(lightThemeClass, learnContainer)}>
      <div className={learnEntryContent}>
        <div className={learnEntryDescription}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={{
              h1: ({ children }) => (
                <H2 as="h1" className={classNames(EntryMarkdownH2)}>
                  {children}
                </H2>
              ),
              h2: ({ children }) => (
                <H4 as="h3" className={classNames(EntryMarkdownH4)}>
                  {children}
                </H4>
              ),
              ul: ({ children }) => (
                <ul className={EntryMarkdownUl}>{children}</ul>
              ),
              li: ({ children }) => (
                <li className={EntryMarkdownLi}>{children}</li>
              ),
              p: ({ children }) => (
                <Body size="big" className={classNames(EntryMarkdownP)}>
                  {children}
                </Body>
              ),
              ol: ({ children }) => (
                <ol className={EntryMarkdownOl}>{children}</ol>
              ),
              table: ({ children }) => (
                <table className={EntryMarkdownTable}>{children}</table>
              ),
              tr: ({ children }) => (
                <tr className={EntryMarkdownTr}>{children}</tr>
              ),
              td: ({ children }) => (
                <td className={EntryMarkdownTd}>{children}</td>
              ),
              th: ({ children }) => (
                <th className={EntryMarkdownTh}>{children}</th>
              ),
              tbody: ({ children }) => (
                <tbody className={EntryMarkdownTbody}>{children}</tbody>
              ),
              thead: ({ children }) => (
                <thead className={EntryMarkdownThead}>{children}</thead>
              ),
            }}
          >
            {entry.data.article}
          </ReactMarkdown>
        </div>

        <div className={otherGlossaryTermsContainer}>
          <H2>Other Glossary Terms</H2>
          <SearchBar
            variant="primary"
            height="64px"
            placeholder="Search..."
            value={searchText}
            onChange={(value) => setSearchText(value ?? '')}
            changeBorderColorOnFocus={false}
            className={otherGlossaryTermsSearchBar}
          />
          <EntriesSection entries={filteredEntries} />
        </div>
      </div>
    </main>
  );
};
